/**
 * Staff Request Workflow Component
 * Displays approval workflow and onboarding progress
 */

import React, { useState } from 'react';
import { Card, Button, Badge, LoadingSpinner } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAPIClient, listUsers } from '@tracekit/core';
import { useToastStore } from '@tracekit/core';
import {
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Loader,
  ArrowRight,
  Mail,
  Bell,
  Info,
} from 'lucide-react';

interface StaffRequest {
  id: string;
  entity_type: string;
  data: {
    project_id: string;
    opportunity_id?: string;
    required_designations?: string[];
    required_expertise?: string[];
    min_experience_years?: number;
    location_requirements?: {
      province?: string;
      city?: string;
    };
    justification: string;
    status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'onboarding' | 'completed';
    requested_by?: string;
    reviewer_id?: string;
    reviewed_at?: string;
    review_notes?: string;
    created_at: string;
  };
  created_at?: string;
}

interface StaffRequestWorkflowProps {
  projectId: string;
  requestId?: string;
  onRequestUpdate?: () => void;
}

export function StaffRequestWorkflow({
  projectId,
  requestId,
  onRequestUpdate,
}: StaffRequestWorkflowProps) {
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();
  const [selectedReviewer, setSelectedReviewer] = useState<string>('');

  // Fetch staff requests
  const { data: requestsData, isLoading } = useQuery<{
    requests: StaffRequest[];
    total: number;
  }>({
    queryKey: ['staff-requests', projectId],
    queryFn: async () => {
      const api = getAPIClient();
      const response = await api.get('/api/rfp/staff/requests', {
        params: { project_id: projectId },
      });
      return response;
    },
    enabled: !!projectId,
  });

  // Fetch users with Admin/Admin Tech roles for reviewer selection
  const { data: adminUsers } = useQuery<{ users: any[] }>({
    queryKey: ['admin-users', projectId],
    queryFn: async () => {
      const response = await listUsers({ project_id: projectId, active_only: true });
      // Filter for Admin/Admin Tech roles
      const admins = response.users.filter(user => {
        const roles = user.tenant_assignments?.map((a: any) => a.role_name) || [];
        return roles.some((r: string) => 
          r === 'keystone_rfp_admin' || r === 'keystone_rfp_admin_tech'
        );
      });
      return { users: admins };
    },
    enabled: !!projectId,
  });

  // Update request mutation
  const updateRequestMutation = useMutation({
    mutationFn: async ({ requestId, data }: { requestId: string; data: any }) => {
      const api = getAPIClient();
      const response = await api.patch(`/api/rfp/staff/requests/${requestId}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-requests', projectId] });
      addToast({
        type: 'success',
        title: 'Request Updated',
        message: 'Staff request has been updated successfully.',
      });
      onRequestUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: error?.response?.data?.detail || error?.message || 'Unknown error',
      });
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'approved':
        return <UserCheck className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <UserX className="w-5 h-5 text-red-600" />;
      case 'onboarding':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'under_review':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'onboarding':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  const handleStatusChange = (requestId: string, newStatus: string, notes?: string) => {
    updateRequestMutation.mutate({
      requestId,
      data: {
        status: newStatus,
        reviewer_id: selectedReviewer || undefined,
        notes: notes || undefined,
      },
    });
  };

  if (isLoading) {
    return (
      <Card className="p-8">
        <LoadingSpinner />
      </Card>
    );
  }

  const requests = requestsData?.requests || [];
  const selectedRequest = requestId ? requests.find(r => r.id === requestId) : null;

  return (
    <div className="space-y-6">
      {/* Requests List */}
      <div>
        <h3 className="font-semibold mb-4">Staff Requests ({requests.length})</h3>
        <div className="space-y-3">
          {requests.map((request) => {
            const reqData = request.data;
            const isSelected = requestId === request.id;
            
            return (
              <Card
                key={request.id}
                className={`p-4 border-2 ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getStatusIcon(reqData.status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">Request #{request.id.substring(0, 8)}</span>
                        <Badge className={`text-xs ${getStatusColor(reqData.status)}`}>
                          {reqData.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      {reqData.required_designations && reqData.required_designations.length > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Designations: {reqData.required_designations.join(', ')}
                        </div>
                      )}
                      
                      {reqData.justification && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 line-clamp-2">
                          {reqData.justification}
                        </p>
                      )}
                      
                      <div className="text-xs text-gray-500 mt-2">
                        Created: {new Date(reqData.created_at).toLocaleDateString()}
                        {reqData.reviewer_id && (
                          <span className="ml-3">Reviewer: {reqData.reviewer_id.substring(0, 8)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Workflow Actions */}
                {reqData.status === 'pending' && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <select
                        value={selectedReviewer}
                        onChange={(e) => setSelectedReviewer(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Select Reviewer (Admin/Admin Tech)</option>
                        {adminUsers?.users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.full_name || user.username} ({user.email})
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(request.id, 'under_review')}
                        disabled={!selectedReviewer}
                      >
                        Assign Reviewer
                      </Button>
                    </div>
                  </div>
                )}

                {reqData.status === 'under_review' && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(request.id, 'approved', 'Approved by reviewer')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(request.id, 'rejected', 'Rejected by reviewer')}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}

                {reqData.status === 'approved' && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(request.id, 'onboarding')}
                    >
                      <Loader className="w-4 h-4 mr-1" />
                      Start Onboarding
                    </Button>
                  </div>
                )}

                {reqData.status === 'onboarding' && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Onboarding in progress...</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => handleStatusChange(request.id, 'completed')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Mark Complete
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Workflow Stages Info */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-2">Approval Workflow:</p>
            <div className="flex items-center gap-2 text-xs space-x-2">
              <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded">Pending</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded">Under Review</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded">Approved/Rejected</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded">Onboarding</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded">Completed</span>
            </div>
            <p className="mt-2 text-xs">
              Notifications and emails are sent at each stage to relevant users (requester, reviewer, admins).
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
